/**
 * AsterAI / ChainAI API client.
 *
 * Phase 1 is read-only. All endpoints require an authenticated merchant user;
 * the backend resolves business_id and role from the JWT cookie.
 */
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/ai`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// The back office signs in with a bearer token (localStorage 'backoffice_token'),
// not a cookie: send it on every AI call.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('backoffice_token');
  if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const AIProduct = {
  ASTERPOS: 'ASTERPOS',
  CHAIN29: 'CHAIN29',
};

export async function askAi({ query, product, context }) {
  const { data } = await api.post('/command', { query, product, context });
  return data;
}

export async function fetchSuggestions(product) {
  const { data } = await api.get('/suggestions', { params: { product } });
  return data?.suggestions ?? [];
}

export async function fetchAuditLog({ limit = 50, offset = 0 } = {}) {
  const { data } = await api.get('/audit-log', { params: { limit, offset } });
  return data;
}

export async function fetchOpsDashboard() {
  const { data } = await api.get('/ops-dashboard');
  return data;
}

export async function previewCampaign({ message, inactiveDays = 60, minPastOrders = 2 } = {}) {
  const { data } = await api.post('/campaign/preview', {
    message: message || null,
    inactive_days: inactiveDays,
    min_past_orders: minPastOrders,
  });
  return data;
}

export async function sendCampaign({ message, inactiveDays = 60, minPastOrders = 2, dry_run = false } = {}) {
  const { data } = await api.post('/campaign/send', {
    message: message || null,
    inactive_days: inactiveDays,
    min_past_orders: minPastOrders,
    dry_run,
  });
  return data;
}

export async function fetchCampaignAuditLog({ limit = 50, offset = 0 } = {}) {
  const { data } = await api.get('/campaign/audit-log', { params: { limit, offset } });
  return data;
}
